import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { on } from "@ember/modifier";
import { htmlSafe, type TrustedHTML } from "@ember/template";

import { type Image, ImageGrid, type PreserveMode } from "#src/index.ts";

import { images } from "../images.ts";

export default class Playground extends Component {
  @tracked preserve: PreserveMode = "aspect-ratio";
  @tracked aggressive = false;
  @tracked count = images.length;
  @tracked width = 100;
  @tracked rowHeight = 140;
  @tracked gap = 8;

  get shownImages(): Image[] {
    return images.slice(0, this.count);
  }

  get preserveIsAspectRatio(): boolean {
    return this.preserve === "aspect-ratio";
  }

  get preserveIsHeight(): boolean {
    return this.preserve === "height";
  }

  get stageStyle(): TrustedHTML {
    return htmlSafe(`width: ${this.width}%;`);
  }

  // The invocation the current controls map to, shown so the knobs and the
  // real component arguments stay connected in the reader's head.
  get snippet(): string {
    let invocation = "<ImageGrid @images={{images}}";

    if (this.preserveIsHeight) invocation += ' @preserve="height"';
    if (this.aggressive) invocation += " @aggressive={{true}}";
    if (this.rowHeight !== 140) invocation += ` @rowHeight={{${this.rowHeight}}}`;
    if (this.gap !== 8) invocation += ` @gap={{${this.gap}}}`;

    return invocation + " />";
  }

  setPreserve = (event: Event): void => {
    this.preserve = (event.target as HTMLInputElement).value as PreserveMode;
  };

  toggleAggressive = (event: Event): void => {
    this.aggressive = (event.target as HTMLInputElement).checked;
  };

  setCount = (event: Event): void => {
    this.count = Number((event.target as HTMLInputElement).value);
  };

  setWidth = (event: Event): void => {
    this.width = Number((event.target as HTMLInputElement).value);
  };

  setRowHeight = (event: Event): void => {
    this.rowHeight = Number((event.target as HTMLInputElement).value);
  };

  setGap = (event: Event): void => {
    this.gap = Number((event.target as HTMLInputElement).value);
  };

  <template>
    <form class="demo-form">
      <div class="control">
        <span class="control-label">Preserve</span>
        <label>
          <input
            type="radio"
            name="preserve"
            value="aspect-ratio"
            checked={{this.preserveIsAspectRatio}}
            {{on "change" this.setPreserve}}
          />
          aspect-ratio
        </label>
        <label>
          <input
            type="radio"
            name="preserve"
            value="height"
            checked={{this.preserveIsHeight}}
            {{on "change" this.setPreserve}}
          />
          height
        </label>
      </div>

      <label class="control">
        <input type="checkbox" checked={{this.aggressive}} {{on "change" this.toggleAggressive}} />
        Aggressive
      </label>

      <label class="control">
        <span class="control-label">Images</span>
        <input
          type="range"
          min="1"
          max={{images.length}}
          value={{this.count}}
          {{on "input" this.setCount}}
        />
        <span class="control-value">{{this.count}}</span>
      </label>

      <label class="control">
        <span class="control-label">Width</span>
        <input type="range" min="30" max="100" value={{this.width}} {{on "input" this.setWidth}} />
        <span class="control-value">{{this.width}}%</span>
      </label>

      <label class="control">
        <span class="control-label">Row height</span>
        <input
          type="range"
          min="80"
          max="260"
          value={{this.rowHeight}}
          {{on "input" this.setRowHeight}}
        />
        <span class="control-value">{{this.rowHeight}}px</span>
      </label>

      <label class="control">
        <span class="control-label">Gap</span>
        <input type="range" min="0" max="32" value={{this.gap}} {{on "input" this.setGap}} />
        <span class="control-value">{{this.gap}}px</span>
      </label>
    </form>

    <p class="playground-snippet"><code>{{this.snippet}}</code></p>

    <div class="playground-stage" style={{this.stageStyle}}>
      <ImageGrid
        @images={{this.shownImages}}
        @preserve={{this.preserve}}
        @aggressive={{this.aggressive}}
        @rowHeight={{this.rowHeight}}
        @gap={{this.gap}}
        as |image|
      >
        <img src={{image.src}} alt="" loading="lazy" />
      </ImageGrid>
    </div>
  </template>
}
